import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import {
  IAmendIncidentDueDateDTO,
  IIncidentCloseDTO,
  IIncidentClosureReasonDTO,
  IIncidentDto,
  IIncidentNotificationCounts,
  IIncidentStatistics,
  IIncidentTypeDTO,
  IIncidentTypeServiceGroupAssigneeDTO,
  IJobIncidentRaiseDTO,
  IManualIncidentRaiseDTO,
} from '../interfaces/incident';
import { IIncidentFilter } from '../interfaces/incidents-page-filter';
import { IPageResult } from '../interfaces/page-result';
import { BaseDataService } from './base.data.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SelectItem } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import { IDropdownItem } from '../interfaces/drop-down-item';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import moment from 'moment';

const pollingInterval = 1000 * 120; // poll every 2 minutes

const pollingIdleTimeout = 1000 * 10 * 60; // stop polling after 10 minutes of idle time

@Injectable({ providedIn: 'root' })
export class IncidentsService extends BaseDataService {
  protected url: string = '/api/incidents';
  protected entityTitle: string = 'Incident';

  get notificationCountsPolling(): Observable<IIncidentNotificationCounts> {
    return this._notificationCountsPollingSubject.asObservable().pipe(debounceTime(500));
  }

  private _notificationCountsPollingSubject: Subject<IIncidentNotificationCounts>;
  private _refreshNotificationCountsSubject = new Subject<IIncidentNotificationCounts>();

  private _notificationCountsPollingSubscription: Subscription;

  private _notificationCountsPollingPaused: boolean = false;

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
    private idle: Idle,
  ) {
    super(http, handlersFactory);

    this._notificationCountsPollingSubject = new BehaviorSubject<IIncidentNotificationCounts>(null);

    this._refreshNotificationCountsSubject = new Subject<IIncidentNotificationCounts>();
    this._refreshNotificationCountsSubject
      .asObservable()
      .pipe(
        debounceTime(500),
        switchMap(() => this.getNotificationCounts()),
      )
      .subscribe((res) => {
        this._notificationCountsPollingSubject.next(res);
      });

    idle.setIdleName('incidents-stats');

    idle.setIdle(pollingIdleTimeout);
    idle.setTimeout(false);

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => this.resumeNotificationCountsPolling());
    idle.onIdleStart.subscribe(() => this.pauseNotificationCountsPolling());
  }

  startNotificationCountsPolling() {
    if (this._notificationCountsPollingSubscription) {
      return;
    }
    let pollingObservable = timer(0, pollingInterval);
    this._notificationCountsPollingSubscription = pollingObservable.subscribe(() => {
      this.refreshStatistics();
    });
    this.idle.watch();
  }

  pauseNotificationCountsPolling() {
    if (this._notificationCountsPollingSubscription) {
      this._notificationCountsPollingPaused = true;
      this._notificationCountsPollingSubscription.unsubscribe();
      this._notificationCountsPollingSubscription = null;
    }
  }

  resumeNotificationCountsPolling() {
    // Check that statistics polling has in fact been running previously
    if (this._notificationCountsPollingPaused) {
      this.startNotificationCountsPolling();
    }
  }

  getPage(filterCommand: IIncidentFilter): Promise<IPageResult<IIncidentDto>> {
    return this.handleRequest<IPageResult<IIncidentDto>>(
      this.http.get<IPageResult<IIncidentDto>>(this.url, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  getPageForComponent(filterCommand: IIncidentFilter): Promise<IPageResult<IIncidentDto>> {
    return this.handleRequest<IPageResult<IIncidentDto>>(
      this.http.get<IPageResult<IIncidentDto>>(this.url, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  getIncidentTypes(): Promise<IIncidentTypeDTO[]> {
    return this.handleRequest<IIncidentTypeDTO[]>(
      this.http.get<IIncidentTypeDTO[]>(`${this.url}/getIncidentTypes`),
      this.handlersFactory.getDefault(),
    );
  }

  getCustomTypes(): Promise<SelectItem[]> {
    return this.handleRequest<SelectItem[]>(
      this.http.get<SelectItem[]>(`${this.url}/custom-types`),
      this.handlersFactory.getDefault(),
    );
  }

  getAssignees(): Promise<IDropdownItem[]> {
    return this.handleRequest<any[]>(
      this.http.get<IDropdownItem[]>(`${this.url}/assignees`),
      this.handlersFactory.getDefault(),
    );
  }

  getCustomers(): Promise<SelectItem[]> {
    return this.handleRequest<SelectItem[]>(
      this.http.get<SelectItem[]>(`${this.url}/getCustomers`),
      this.handlersFactory.getDefault(),
    );
  }

  getSuppliers(): Promise<SelectItem[]> {
    return this.handleRequest<SelectItem[]>(
      this.http.get<SelectItem[]>(`${this.url}/getSuppliers`),
      this.handlersFactory.getDefault(),
    );
  }

  getClosureReasonsForDropdown(): Promise<SelectItem[]> {
    return this.handleRequest<SelectItem[]>(
      this.http.get<SelectItem[]>(`${this.url}/closureReasonsForDropdown`),
      this.handlersFactory.getDefault(),
    );
  }

  getClosureReasons(): Promise<IIncidentClosureReasonDTO[]> {
    return this.handleRequest<IIncidentClosureReasonDTO[]>(
      this.http.get<IIncidentClosureReasonDTO[]>(`${this.url}/closureReasons`),
      this.handlersFactory.getDefault(),
    );
  }

  refreshStatistics() {
    return this._refreshNotificationCountsSubject.next(null);
  }

  getStatistics(): Promise<IIncidentStatistics> {
    return this.handleRequest<IIncidentStatistics>(
      this.http.get<IIncidentStatistics>(`${this.url}/statistics`),
      this.handlersFactory.getDefault(),
    );
  }

  getNotificationCounts(): Promise<IIncidentNotificationCounts> {
    return this.handleRequest<IIncidentNotificationCounts>(
      this.http.get<IIncidentNotificationCounts>(`${this.url}/notificationCounts`),
      this.handlersFactory.getDefault(),
    );
  }

  assigneeToMe(id: number) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/assignee-to-me/${id}`, null),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  assigneeToUser(id: number, userId: string) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/assignee-to-user/${id}/${userId}`, null),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  assigneeOpenToUser(typeId: number, userId: string) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/assign-open-to-user/${typeId}/${userId}`, null),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  raiseJobIncident(data: IJobIncidentRaiseDTO): Promise<any> {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/raise`, data),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  raiseManualIncident(data: IManualIncidentRaiseDTO): Promise<any> {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/raise-manual`, data),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  addClosureReason(reason: IIncidentClosureReasonDTO): Promise<any> {
    return this.http.post(`${this.url}/addClosureReason`, reason).toPromise();
  }

  activateClosureReason(id: number) {
    return this.http.put(`${this.url}/activateClosureReason/${id}`, {}).toPromise();
  }

  deactivateClosureReason(id: number) {
    return this.http.put(`${this.url}/deactivateClosureReason/${id}`, {}).toPromise();
  }

  activateIncidentType(id: number) {
    return this.http.put(`${this.url}/activateIncidentType/${id}`, {}).toPromise();
  }

  deactivateIncidentType(id: number) {
    return this.http.put(`${this.url}/deactivateIncidentType/${id}`, {}).toPromise();
  }

  patchIncidentType(id: number, patch: any) {
    return this.http.patch(`${this.url}/${id}`, patch).toPromise();
  }

  setIncidentTypeAssignee(dto: IIncidentTypeServiceGroupAssigneeDTO) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/set-incident-type-assignee`, dto),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  deleteIncidentTypeAssignee(id: number) {
    return this.handleRequest<any>(
      this.http.delete<any>(`${this.url}/delete-incident-type-assignee/${id}`),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  close(closeDto: IIncidentCloseDTO) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/close`, closeDto),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  groupClose(closeDtos: IIncidentCloseDTO[]) {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}/closeGroup`, closeDtos),
      this.handlersFactory.getDefault(),
    ).then((res) => {
      this.refreshStatistics();
      return res;
    });
  }

  downloadToCSV(filter: IIncidentFilter, isOpen: boolean) {
    let filename =
      (isOpen ? 'Open' : 'Closed') + ' Incidents_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';

    let params = this.constructHttpParams(filter);
    let report = this.http.get(`${this.url}/downloadToCSV`, {
      params: params,
      responseType: 'blob',
    });

    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Unable to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  getIncidentTypeServiceGroupAssignees(): Promise<IIncidentTypeServiceGroupAssigneeDTO[]> {
    return this.handleRequest<IIncidentTypeServiceGroupAssigneeDTO[]>(
      this.http.get<IIncidentTypeServiceGroupAssigneeDTO[]>(
        `${this.url}/incidentTypeServiceGroupAssignees`,
      ),
      this.handlersFactory.getDefault(),
    );
  }

  amendDueDate(dueDateDto: IAmendIncidentDueDateDTO) {
    return this.handleRequest<any>(
      this.http.put<any>(`${this.url}/amendDueDate`, dueDateDto),
      this.handlersFactory.getDefault(),
    );
  }
}
